<template>
  <div class="farm-detail-page">
    <a-card class="main-container" :loading="loading">
      <!-- Top Bar - 牧场名称 + 操作按钮 在同一行圆角容器里 -->
      <div class="top-bar">
        <div class="top-bar-left">
          <h2 class="farm-name">{{ farm.name }}</h2>
        </div>
        <div class="top-bar-right">
          <a-button v-if="cell_query.group_type == 'cell'" type="primary" @click="edit_farm_cell('add', farm, null, null)" class="add-btn">
            <a-icon type="plus" />新增群
          </a-button>
          <a-button v-if="cell_query.group_type == 'cell'" type="default" @click="open_add_daily_production" class="add-btn">
            <a-icon type="file-add" />添加日产记录
          </a-button>
          <a-button v-if="cell_query.group_type == 'selectRecord'" type="primary" @click="edit_select_record('add', farm, null)" class="add-btn">
            <a-icon type="plus" />新增选配记录
          </a-button>
          <a-radio-group v-model="cell_query.group_type" button-style="solid" @change="on_view_change" class="view-toggle">
            <a-radio-button value="cell">栏舍视图</a-radio-button>
            <a-radio-button value="selectRecord">选配记录</a-radio-button>
          </a-radio-group>
        </div>
      </div>
      
      <!-- Group Filter Tabs -->
      <div class="group-filter-bar" v-if="cell_query.group_type == 'cell' && farm_groups.l1_farm_cells && farm_groups.l1_farm_cells.length > 0">
        <div class="filter-tabs">
          <div 
            v-for="(l1_cell, l1_index) in farm_groups.l1_farm_cells" 
            :key="l1_index"
            :class="['filter-tab', { active: active_group_id === l1_cell.id }]"
            @click="select_group(l1_cell.id)"
          >
            <span class="filter-name">{{ l1_cell.name }}</span>
            <span class="filter-count">{{ l1_cell.l2_farm_cells ? l1_cell.l2_farm_cells.length : 0 }}</span>
          </div>
        </div>
        <a-button type="link" class="refresh-btn" @click="refresh_list">
          <a-icon type="reload" />
        </a-button>
      </div>
      
      <!-- Content Section -->
      <div class="content-section">
        <a-spin v-if="loading" class="loading-spinner" />
        
        <a-empty description="暂无分群" v-if="(!farm_groups || !farm_groups.l1_farm_cells || farm_groups.l1_farm_cells.length == 0) && cell_query.group_type != 'selectRecord' && !loading" />
        
        <!-- Cell Groups -->
        <div v-for="(l1_cell, l1_index) in farm_groups.l1_farm_cells" :key="l1_index" class="cell-group" v-if="cell_query.group_type == 'cell' && (active_group_id === null || active_group_id === l1_cell.id)">
          <div class="group-header">
            <div class="group-title">
              <span class="group-icon">
                <a-icon type="appstore" />
              </span>
              <span class="group-name">{{ l1_cell.name }}</span>
            </div>
            <div class="group-actions">
              <a-button type="link" class="action-link danger" @click="delete_farm_cell('edit', farm, l1_cell)">
                <a-icon type="delete" />
              </a-button>
              <a-button type="link" class="action-link" @click="edit_farm_cell('edit', farm, l1_cell)">
                <a-icon type="edit" />
              </a-button>
              <a-button type="primary" size="small" @click="edit_l2_farm_cell('add', farm, null, l1_cell)" class="add-sub-btn">
                <a-icon type="plus" />新增子群
              </a-button>
            </div>
          </div>
          
          <div class="cells-grid">
            <a-empty description="暂无群舍" v-if="l1_cell.l2_farm_cells.length == 0" class="empty-state" />
            <div v-for="(l2_cell, l2_index) in l1_cell.l2_farm_cells" :key="l2_index" class="cell-card">
              <div class="cell-content" @click="open_cell(l2_cell.id)">
                <div class="cell-name">{{ l2_cell.name }}</div>
                <div class="cell-count">
                  <span class="count-label">总数</span>
                  <span class="count-value">{{ l2_cell.item_count }}</span>
                  <span class="count-unit">只</span>
                </div>
              </div>
              <div class="cell-actions">
                <a-tooltip title="编辑">
                  <a-button type="link" size="small" class="cell-action-btn" @click.stop="edit_farm_cell('edit', farm, l2_cell)">
                    <a-icon type="edit" />
                  </a-button>
                </a-tooltip>
                <a-tooltip title="删除">
                  <a-button type="link" size="small" class="cell-action-btn danger" @click.stop="delete_farm_cell(l2_cell.id)">
                    <a-icon type="delete" />
                  </a-button>
                </a-tooltip>
              </div>
            </div>
          </div>
        </div>

        <!-- Select Records -->
        <div v-if="cell_query.group_type == 'selectRecord'" class="select-records-container">
          <a-empty v-if="select_records.length === 0 && !loading" description="暂无选配记录" />
          <div v-for="(record, index) in select_records" :key="index">
            <select-record-row
              :record="record"
              :show-date-separator="index == 0 || record.created_at.substring(0, 10) != select_records[index - 1].created_at.substring(0, 10)"
              @open-cell="open_cell(record.id, 'selectRecord')"
              @delete-record="delete_select_record"
            ></select-record-row>
          </div>
          <a-pagination v-if="select_records.length > 0" v-model="cell_pagination.current"
            class="pagination"
            :pageSize="cell_query.page_size"
            :showTotal="total => `共${total}条`" :total="cell_pagination.total"
            @change="select_records_page_change"></a-pagination>
        </div>
      </div>
      
      <a-empty description="暂无牧场" v-if="!farm && !loading" />
    </a-card>

    <addOrEditCell ref="addOrEditCell" @finish-edit="refresh_list"></addOrEditCell>
    <addOrEditSelectRecord ref="addOrEditSelectRecord" @finish-edit="refresh_list"></addOrEditSelectRecord>
    <addDailyProductionRecord ref="addDailyProductionRecord" @success="refresh_list"></addDailyProductionRecord>
  </div>
</template>

<script>
import addOrEditCell from './components/addOrEditCell.vue'
import addOrEditSelectRecord from './components/addOrEditSelectRecord.vue';
import selectRecordRow from './components/selectRecordRow.vue';
import addDailyProductionRecord from './components/addDailyProductionRecord.vue';
export default ({
  components: { addOrEditCell, addOrEditSelectRecord, selectRecordRow, addDailyProductionRecord},
  data() {
    return {
      list: [],
      searchKeyword: '',
      active_group_id: null,
      query: {
        name: '',
        page_no: 1,
        page_size: 20
      },
      cell_query: {
        farm_id: '',
        group_type: 'cell',
        page_no: 1,
        page_size: 20
      },
      cell_pagination: {
        current: 1,
        total_pages: 1,
        total: 0
      },
      select_records: [],
      loading: true,
      farm: {},
      farm_groups: {},
      groups: [],
      headStyle: {
        backgroundColor: '#EEF5FE ',
        color: '#333',
        "border-radius": '10px 10px 0px 0px',
        "font-weight": 'bold'
      }
    }
  },
  async created() {
    this.loadFarmData(this.$route.params.id)
  },
  watch: {
    // 监听路由参数变化，解决同组件复用时数据不更新的问题
    '$route.params.id': {
      handler(newId, oldId) {
        if (newId && newId !== oldId) {
          this.loadFarmData(newId)
        }
      }
    }
  },
  methods: {
    // 加载牧场数据
    loadFarmData(id) {
      if (id) {
        this.loading = true
        this.$api.get_farms({ id: id }).then(res => {
          if (res.code == 200) {
            this.farm = res.data.list[0]
            this.cell_query.farm_id = id
            this.active_group_id = null // 重置选中的分组
            this.refresh_list()
          }
        })
      }
    },
    open_farm(farm) {
      this.farm = farm
      this.cell_query.farm_id = farm.id
      this.cell_query.page_no = 1
      this.refresh_list()
    },
    get_farms(query) {
      this.$api.get_farms(query).then(res => {
        this.list = res.data.list
        if (this.farm.id == undefined && this.list.length > 0) {
          this.farm = this.list[0]
        }
        if (this.groups.length == 0) {
          this.cell_query.farm_id = this.list[0].id
          this.refresh_list()
        }
      })
    },
    open_cell(id, group_type='cell') {
      this.$router.push({
        path: `/farms/cell/${id}?group_type=${group_type}`
      })
    },
    edit_farm(action, item) {
      this.$refs.addOrEdit.open(action, item)
    },
    refresh_list() {
      this.loading = true
      this.farm_groups = {}
      if (['cell', 'tag'].includes(this.cell_query.group_type)) {
        this.$api.get_farm_groups(this.cell_query).then(res => {
          if (res.code == 200) {
            this.farm_groups = res.data
            this.loading = false
          }
        })
      } else {
        const payload = {
          farm_id: this.cell_query.farm_id,
          page_no: this.cell_query.page_no,
          page_size: this.cell_query.page_size
        }
        this.$api.get_item_select_records(payload).then(res => {
          this.loading = false
          if (res.code == 200) {
            this.select_records = res.data
            this.cell_pagination.current = res.page.page_no
            this.cell_pagination.total_pages = res.page.total_pages
            this.cell_pagination.total = res.page.total
          }
        })
      }
    },
    select_records_page_change(e) {
      this.cell_query.page_no = e
      this.refresh_list()
    },
    edit_farm_cell(action, farm, item, parent) {
      this.$refs.addOrEditCell.open(action, farm, item, parent?.id)
    },
    edit_l2_farm_cell(action, farm, item, l1_cell) {
      this.$refs.addOrEditCell.open(action, farm, item, l1_cell?.id)
    },
    edit_select_record(action, farm, item) {
      this.$refs.addOrEditSelectRecord.open(action, farm, item)
    },
    open_add_daily_production() {
      this.$refs.addDailyProductionRecord.open({
        farm_id: this.farm.id
      });
    },
    delete_farm_cell(id) {
      const _self = this
      this.$confirm({
        title: '确认删除该子群？',
        okText: '确认',
        cancelText: '取消',
        onOk() {
          _self.$api.delete_farm_cell(id).then(res => {
            if (res.code == 200) {
              _self.$message.success('删除成功')
              _self.refresh_list()
            }
          })
        }
      })
    },
    on_view_change(val) {
      this.cell_query.group_type = val.target.value
      this.refresh_list()
    },
    on_select_farm(farm) {

    },
    delete_farm_cell(mode, farm, l1_cell) {
      const _self = this
      this.$confirm({
        title: '确认删除该子群？',
        okText: '确认',
        cancelText: '取消',
        onOk() {
          _self.$api.delete_farm_cell(l1_cell.id).then(res => {
            if (res.code == 200) {
              _self.$message.success('删除成功')
            }
          })
        }
      })
    },
    delete_select_record(id) {
      const _self = this
      this.$confirm({
        title: '确认删除该记录？',
        okText: '确认',
        cancelText: '取消',
        onOk() {
          _self.$api.delete_item_select_record_by_id(id).then(res => {
            if (res.code == 200) {
              _self.$message.success('删除成功')
              _self.refresh_list()
            }
          })
        }
      })
    },
    on_search(value) {
      // 搜索功能：可以根据名称过滤栏舍
      console.log('搜索:', value)
    },
    select_group(group_id) {
      this.active_group_id = this.active_group_id === group_id ? null : group_id
    }
  }
})
</script>

<style lang="scss" scoped>
.farm-detail-page {
  padding: 0;
  width: 100%;
}

.main-container {
  border-radius: 12px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.06);
  border: none;
  width: 100%;
}

.main-container ::v-deep .ant-card-body {
  padding: 20px;
  width: 100%;
}

/* Top Bar - 牧场名称 + 操作按钮在同一行圆角容器 */
.top-bar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: #f5f7fa;
  border-radius: 10px;
  padding: 12px 20px;
  margin-bottom: 16px;
}

.top-bar-left {
  display: flex;
  align-items: center;
}

.farm-name {
  margin: 0;
  font-size: 18px;
  font-weight: 600;
  color: #1a1a1a;
}

.top-bar-right {
  display: flex;
  align-items: center;
  gap: 12px;
}

.add-btn {
  border-radius: 6px;
  height: 36px;
  padding: 0 16px;
  font-weight: 500;
  box-shadow: 0 2px 6px rgba(37, 99, 235, 0.2);
}

.add-btn ::v-deep .anticon {
  margin-right: 6px;
}

.view-toggle {
  background: #fff;
  border-radius: 8px;
  padding: 3px;
}

.view-toggle ::v-deep .ant-radio-button-wrapper {
  border-radius: 6px;
  border: none;
  padding: 0 20px;
  height: 30px;
  line-height: 28px;
  transition: all 0.3s;
  font-size: 13px;
}

.view-toggle ::v-deep .ant-radio-button-wrapper::before {
  display: none;
}

.view-toggle ::v-deep .ant-radio-button-wrapper-checked {
  background: #2563EB;
  color: #fff;
  box-shadow: 0 2px 6px rgba(37, 99, 235, 0.3);
  border: none;
}

.view-toggle ::v-deep .ant-radio-button-wrapper-checked::before {
  display: none;
}

/* Group Filter Bar */
.group-filter-bar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
  padding: 0 4px;
}

.filter-tabs {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
  flex: 1;
}

.filter-tab {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 6px 14px;
  background: #f5f7fa;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.2s ease;
  font-size: 13px;
  border: 1px solid transparent;
}

.filter-tab:hover {
  background: #eef2ff;
  border-color: #c7d7fe;
}

.filter-tab.active {
  background: #2563EB;
  color: #fff;
  border-color: #2563EB;
  box-shadow: 0 2px 6px rgba(37, 99, 235, 0.25);
}

.filter-name {
  font-weight: 500;
}

.filter-count {
  background: rgba(0, 0, 0, 0.06);
  padding: 1px 8px;
  border-radius: 10px;
  font-size: 12px;
  font-weight: 600;
}

.filter-tab.active .filter-count {
  background: rgba(255, 255, 255, 0.25);
}

.refresh-btn {
  color: #8c8c8c;
  padding: 4px 8px;
  margin-left: 12px;
}

.refresh-btn:hover {
  color: #2563EB;
}

/* Content Section */
.content-section {
  min-height: 200px;
  width: 100%;
}

.loading-spinner {
  display: flex;
  justify-content: center;
  padding: 40px;
}

/* Cell Groups */
.cell-group {
  background: #fafbfc;
  border-radius: 12px;
  padding: 20px;
  margin-bottom: 20px;
  border: 1px solid #eaeaea;
  transition: all 0.3s ease;
}

.cell-group:hover {
  border-color: #d9d9d9;
}

.group-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
  padding-bottom: 12px;
  border-bottom: 1px solid #e8e8e8;
}

.group-title {
  display: flex;
  align-items: center;
  gap: 12px;
}

.group-icon {
  width: 36px;
  height: 36px;
  background: linear-gradient(135deg, #2563EB 0%, #3b82f6 100%);
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  font-size: 16px;
  box-shadow: 0 2px 8px rgba(37, 99, 235, 0.25);
}

.group-name {
  font-size: 16px;
  font-weight: 600;
  color: #262626;
}

.group-actions {
  display: flex;
  align-items: center;
  gap: 4px;
}

.action-link {
  color: #8c8c8c;
  padding: 6px 10px;
  transition: all 0.2s;
  border-radius: 6px;
}

.action-link:hover {
  color: #2563EB;
  background: rgba(37, 99, 235, 0.08);
}

.action-link.danger:hover {
  color: #ff4d4f;
  background: rgba(255, 77, 79, 0.08);
}

.add-sub-btn {
  border-radius: 6px;
  padding: 0 14px;
  height: 32px;
  margin-left: 8px;
}

.add-sub-btn ::v-deep .anticon {
  margin-right: 4px;
}

/* Cells Grid */
.cells-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(220px, 1fr));
  gap: 16px;
}

.empty-state {
  grid-column: 1 / -1;
  padding: 40px;
}

/* Cell Card */
.cell-card {
  background: #fff;
  border-radius: 10px;
  padding: 16px 20px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04);
  border: 1px solid #eaeaea;
  transition: all 0.25s ease;
  cursor: pointer;
}

.cell-card:hover {
  box-shadow: 0 4px 12px rgba(37, 99, 235, 0.15);
  border-color: #2563EB;
  
}

.cell-content {
  flex: 1;
}

.cell-name {
  font-size: 15px;
  font-weight: 600;
  color: #262626;
  margin-bottom: 8px;
}

.cell-count {
  display: flex;
  align-items: baseline;
  gap: 6px;
}

.count-label {
  font-size: 12px;
  color: #8c8c8c;
}

.count-value {
  font-size: 24px;
  font-weight: 700;
  color: #2563EB;
  line-height: 1;
}

.count-unit {
  font-size: 12px;
  color: #8c8c8c;
}

.cell-actions {
  display: flex;
  flex-direction: column;
  gap: 2px;
  margin-left: 12px;
}

.cell-action-btn {
  color: #bfbfbf;
  padding: 4px 6px;
  transition: all 0.2s;
}

.cell-action-btn:hover {
  color: #2563EB;
}

.cell-action-btn.danger:hover {
  color: #ff4d4f;
}

/* Pagination */
.pagination {
  margin-top: 28px;
  text-align: center;
}

.pagination ::v-deep .ant-pagination-item {
  border-radius: 6px;
}

.pagination ::v-deep .ant-pagination-item-active {
  border-color: #2563EB;
}

/* Select Records Section */
.select-records-container {
  background: #fafbfc;
  border-radius: 12px;
  padding: 20px;
  border: 1px solid #f0f0f0;
}

/* Responsive */
@media (max-width: 768px) {
  .top-bar {
    flex-direction: column;
    gap: 12px;
    align-items: flex-start;
  }
  
  .top-bar-left {
    width: 100%;
  }
  
  .top-bar-right {
    width: 100%;
    flex-wrap: wrap;
    gap: 8px;
  }
  
  .view-toggle {
    width: 100%;
    display: flex;
  }
  
  .view-toggle ::v-deep .ant-radio-button-wrapper {
    flex: 1;
    text-align: center;
  }
  
  .group-filter-bar {
    flex-direction: column;
    gap: 12px;
    align-items: flex-start;
  }
  
  .filter-tabs {
    width: 100%;
    overflow-x: auto;
    flex-wrap: nowrap;
    padding-bottom: 4px;
  }
  
  .filter-tab {
    flex-shrink: 0;
  }
  
  .refresh-btn {
    margin-left: 0;
  }
  
  .cells-grid {
    grid-template-columns: 1fr;
  }
  
  .group-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 12px;
  }
  
  .group-actions {
    width: 100%;
    justify-content: flex-start;
    flex-wrap: wrap;
  }
}
</style>

<!-- 非scoped样式，确保样式穿透生效 -->
<style lang="scss">
.farm-detail-page {
  width: 100%;
}

.farm-detail-page .main-container {
  width: 100%;
}

.farm-detail-page .top-bar {
  display: flex !important;
  justify-content: space-between !important;
  align-items: center !important;
  background: #f5f7fa !important;
  border-radius: 10px !important;
  padding: 12px 20px !important;
  margin-bottom: 16px !important;
}

.farm-detail-page .farm-name {
  margin: 0;
  font-size: 18px;
  font-weight: 600;
  color: #1a1a1a;
}

.farm-detail-page .top-bar-right {
  display: flex;
  align-items: center;
  gap: 12px;
}

.farm-detail-page .add-btn {
  border-radius: 6px;
  height: 36px;
}

.farm-detail-page .view-toggle {
  background: #fff;
  border-radius: 8px;
  padding: 3px;
}

.farm-detail-page .view-toggle .ant-radio-button-wrapper {
  border-radius: 6px;
  border: none;
}

.farm-detail-page .filter-tab {
  border-radius: 6px;
}

.farm-detail-page .cell-group {
  border-radius: 12px;
}

.farm-detail-page .cell-card {
  border-radius: 10px;
}

.farm-detail-page .content-section {
  width: 100%;
}

.farm-detail-page .cells-grid {
  width: 100%;
}
</style>